import React, { useRef } from "react";
import { FormControl, InputGroup } from "react-bootstrap";

import Actions from "src/components/actionbar";

import { Account } from "src/types/accounts";

import { getLogoUrl, resizeImage } from "src/services/imageUpload";

import { toast } from "react-toastify";
import Toast from "src/components/toast";

import _ from "lodash";
import styles from "./styles.module.scss";

const DefaultLogo = `/images/settings/defaultLogo.svg`

interface AccountSettingsProps {
  data: Partial<Account>;
  handleAccountSettingsUpdate: (field: string, value: any) => void;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ data, handleAccountSettingsUpdate }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const logoUrl = getLogoUrl(data.logo);

  const handleEditIconClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast(
        <Toast.Failure
          message="File size exceeds 2MB. Please select a smaller image."
        />,
        {
          autoClose: 2000,
        }
      );
      e.target.value = "";
      return;
    }

    const resized = await resizeImage(file, { width: 640, height: 480 });
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result as string;

      handleAccountSettingsUpdate("logo", base64String);
    };

    reader.readAsDataURL(resized);
  };

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div className={styles.inputGroupRow}>
          <InputGroup className={styles.inputGroup}>
            <InputGroup.Text className={styles.inputText}>Company Name</InputGroup.Text>
            <FormControl
              value={data.name || ''}
              className={styles.customInput}
              onChange={(e) => handleAccountSettingsUpdate('name', e.target.value)}
              placeholder="Enter your company name"
            />
          </InputGroup>

          <InputGroup className={styles.inputGroup}>
            <InputGroup.Text className={styles.inputText}>Company URL</InputGroup.Text>
            <FormControl
              value={data.companyUrl || ''}
              className={styles.customInput}
              onChange={(e) => handleAccountSettingsUpdate('companyUrl', e.target.value)}
              placeholder="Enter your company URL"
            />
          </InputGroup>
        </div>
        <div className={styles.logoSection}>
          <div className={styles.inputText}>Company Logo</div>
          <div className={styles.description}>Upload your logo for the Content Hub</div>
          <div className={styles.cardStack}>
            <div className={styles.card}>
              <div className={styles.logoContainer}>
                {logoUrl && <img src={logoUrl} alt="Company Logo" className={styles.logo} />}
                {!logoUrl && (
                  <div className={styles.defaultLogoPlaceholder}>
                    <img src={DefaultLogo} className={styles.defaultLogo} />
                    <div className={styles.defaultLogoText}>Logo here</div>
                  </div>
                )}
                <Actions.Button
                  className={styles.editLogoButton}
                  icon="paperflite-edit"
                  title="Edit Logo"
                  onClick={handleEditIconClick}
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
              <div className={styles.placeholder}>
                <p className={styles.heading}>content hub</p>
                <p className={styles.subHeading}>Your content's natural habitat</p>
              </div>
            </div>
            <div className={styles.overlay}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;